import { AutoFixHigh } from "@mui/icons-material";
import {
  Alert,
  Checkbox,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  SxProps,
  Typography
} from "@mui/material";
import { useAtom, useAtomValue } from "jotai";
import { selectAtom } from "jotai/utils";
import { useEffect, useState } from "react";
import { protectedApiCall } from "../../api/network";
import { AI_GENERATION_PROGRESS, ARTIFACT_TYPES } from "../../constants";
import { baseAtom, setShowCanvasNotes } from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import {
  aiTemplateGenerationAtom,
  setAiTemplateGenerationProgress,
} from "../../stores/jotai/generation-progress/ai/aiTemplateGeneration";
import { hasCanvasWriteAccess } from "../../stores/jotai/permissions";
import yjsObjectToPlainString from "../../util/yjsObjectToPlainString";
import AlertDialog from "../app/alert-dialog";
import IwButton from "../app/IwButton";
import MaterialArtifactEditor from "../app/material-artifact-editor";
import MaterialModal from "../app/material-modal";

type Entry = {
  entryID: string;
  created: string;
  modified: string | null;
  entry: string;
  state: "Hypothesis" | "Confirmed" | "Invalidated"; // Adjust as necessary if there are more states
  stateModified: string | null;
  sortOrder: number;
  modifier: string | null;
  categoryID: string;
  userID: string;
  version: number;
  createdF: string | null;
  modifiedF: string | null;
  parentCategoryID: string | null;
  entryInsertionType: "AI" | "Manual"; // Adjust as necessary if there are more types

};

interface GenerateAITemplateModalProps {
  open: boolean;
  handleDialogClose?: Function;
  entries: Entry[];
  canvas?: object;
  setBase: Function;
}
const TEXT_STYLE: SxProps = {
    fontSize: "15px",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.8)",
}

const teamAtom =  selectAtom(baseAtom, (base) => base.team)
const roleSnapshotAtom = selectAtom(baseAtom, (base) => base.roleSnapshot);
export default function GenerateAITemplateModal({
  handleDialogClose,
  open,
  entries,
  canvas,
  setBase
}: GenerateAITemplateModalProps) {
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  const [checked, setChecked] = useState([]);
  const discoverySession = useAtomValue(discoverySessionAtom);
  const [aiTemplateGenerationState, setAiTemplateGenerationAtom] = useAtom(
    aiTemplateGenerationAtom
  );
  const [businessIdeaTextInput, setBusinessIdeaTextInput] = useState({});
  const [isCSExist, setIsCSExist] = useState(false);
  const [team] = useAtom(teamAtom);
  const [roleSnapshot] = useAtom(roleSnapshotAtom);

  const [templateModals, setTemplateModals] = useState({
    selectionError: false,
  });

  useEffect(() => {
    const customerSegmentCategory = canvas?.categories.find(
      (category) => category?.name === "Customer Segments" && category?.state !== "Deleted"
    );
    const isCustomerSegmentExist = customerSegmentCategory
      ? customerSegmentCategory?.entries?.length > 0
      : false;

    if (!isCustomerSegmentExist) {
      setIsCSExist(true);
    } else {
      const filteredEntryIDs = customerSegmentCategory?.entries?.reduce((acc, entry) => {
        if (entry.state !== "Deleted") {
          acc.push(entry.entryID);
        }
        return acc;
      }, []);

      setChecked(filteredEntryIDs);
      setIsCSExist(false);
    }
  }, [isCSExist, canvas?.categories]);

  const handleGenerateTemplate = (retry = false) => {
    if (checked?.length <= 0) {
      setTemplateModals(prev => ({...prev, selectionError: true}))
    } else {
      setIsGeneratingTemplate(true);
      const groupID = team?.groupID;
      protectedApiCall(
        discoverySession,
        "/generate-interview-template",
        {
          groupID,
          description: businessIdeaTextInput,
          canvasType: canvas?.type,
          entryIDs: checked,
        },
        {},
        "post"
      )
        .then((res) => {
          // Hide the modal
          setShowCanvasNotes(setBase, false);
          handleDialogClose();
          setIsCSExist(false);

          if (!!res?.generationStarted) {
            setAiTemplateGenerationProgress(
              setAiTemplateGenerationAtom,
              groupID,
              AI_GENERATION_PROGRESS.IN_PROGRESS
            );
          } else {
            setAiTemplateGenerationProgress(
              setAiTemplateGenerationAtom,
              groupID,
              AI_GENERATION_PROGRESS.NOT_STARTED
            );
          }
          // if (fromHome) {
          //     pageChange("/canvas");
          // }
        })
        .catch((error) => {
          console.error("Error generating template:", error);
        })
        .finally(() => {
          setIsGeneratingTemplate(false);
        });
    }
  };

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked?.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  return (
    <>
      <AlertDialog
        dialogWidth="400px"
        open={templateModals.selectionError}
        onClose={() => {
          setTemplateModals((prev) => ({ ...prev, selectionError: false }));
        }}
        title="Please select at least one customer segment."
        confirmButton={
          <IwButton
            variant="outlined"
            onClick={() => {
              setTemplateModals((prev) => ({ ...prev, selectionError: false }));
            }}
          >
            Dismiss
          </IwButton>
        }
      />
      <MaterialModal
        title="Generate Interview Templates with AI"
        styleOverrides={{
          container: {
            "& .MuiDialog-paper": { maxWidth: "650px", minWidth: "650px" }
          }
        }}
        open={open}
        handleDialogClose={handleDialogClose}
      >
        <Stack spacing={1} sx={{ minHeight: 350, maxHeight: 350, overflowY: "auto",}}>
          <List sx={{ width: "100%", bgcolor: "background.paper",}}>
            {entries?.map((item) => {
              const labelId = `checkbox-list-label-${item?.entryID}`;

              return (
                <ListItem key={item?.entryID} disablePadding>
                  <ListItemButton
                    role={undefined}
                    onClick={handleToggle(item?.entryID)}
                    dense
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={checked?.includes(item?.entryID)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ "aria-labelledby": labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id={labelId}
                      primary={item?.entry}
                      primaryTypographyProps={{ ...TEXT_STYLE }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Stack>

        <Stack spacing={1} my={2}>
          <div style={{ display: "none" }}>
            <MaterialArtifactEditor
              isReadOnly={!hasCanvasWriteAccess(roleSnapshot)}
              artifactID={team?.groupID}
              artifactType={ARTIFACT_TYPES.CANVAS_NOTE}
              onChange={(e) =>
                setBusinessIdeaTextInput(yjsObjectToPlainString(e))
              }
              useToolBar={false}
            />
          </div>

          {isCSExist ? (
            <Alert severity="warning" variant="outlined">
              You must create your canvas with AI to generate interview
              templates with AI.
            </Alert>
          ) : (
            <>
              <Divider />
              <Typography textAlign="left" sx={{ ...TEXT_STYLE }}>
                With this information, we’ll craft interview templates for each
                of your customer segments. You can always fine-tune the details
                to perfectly align with your vision late
              </Typography>
            </>
          )}
        </Stack>

        <Stack
          // mt={3}
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "flex-end",
            paddingTop: "8px"
          }}
          spacing={1}
        >
          <IwButton
            loading={isGeneratingTemplate}
            variant="contained"
            startIcon={<AutoFixHigh />}
            onClick={() => {
              handleGenerateTemplate();
            }}
            disabled={
              isGeneratingTemplate ||
              isCSExist ||
              aiTemplateGenerationState?.aiTemplateGenerationProgress ===
                AI_GENERATION_PROGRESS.IN_PROGRESS ||
              !roleSnapshot?.isTeamMember
            }
          >
            Generate AI Templates
          </IwButton>
        </Stack>
      </MaterialModal>
    </>
  );
}
